import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { nextTick, onBeforeUnmount, shallowRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import ProductTourOverlay from '@/components/onboarding/ProductTourOverlay.vue';
// eslint-disable-next-line import/no-cycle
import { useProductTourStore } from '@/stores/productTour';


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductTourHost',
  setup(__props) {

const tour = useProductTourStore();
const route = useRoute();
const router = useRouter();
const toast = useToast();
const navigationTarget = shallowRef<string | null>(null);

async function next(): Promise<void> {
    if (navigationTarget.value) return;
    if (tour.phase === 'welcome') {
        tour.start();
        return;
    }

    const nextStep = tour.steps[tour.stepIndex + 1];
    if (!nextStep?.route) {
        tour.next();
        return;
    }

    const { stepIndex } = tour;
    navigationTarget.value = router.resolve(nextStep.route).fullPath;
    try {
        const failure = await router.push(nextStep.route);
        await nextTick();
        if (failure || route.fullPath !== navigationTarget.value) throw new Error('Navigation failed');
        if (tour.isOpen && tour.stepIndex === stepIndex) tour.next();
    } catch {
        if (tour.isOpen) {
            toast.add({ severity: 'error', summary: 'Product tour', detail: 'Unable to open Payments. Please try again or skip the tour.', life: 5000 });
        }
    } finally {
        navigationTarget.value = null;
    }
}

watch(() => tour.replayRequested, async (requested) => {
    if (!requested) return;
    try {
        if (route.name !== 'dashboard') {
            await router.push({ name: 'dashboard' });
        }
        if (route.name === 'dashboard') tour.startReplay();
        else tour.cancelReplay();
    } catch {
        tour.cancelReplay();
        toast.add({ severity: 'error', summary: 'Product tour', detail: 'Unable to open the dashboard. Please try again.', life: 5000 });
    }
});

watch(
    () => [route.name, tour.eligible, tour.shouldOfferWelcome, tour.replayRequested],
    () => {
        if (route.name !== 'dashboard') return;
        if (tour.replayRequested) tour.startReplay();
        else tour.offerWelcome();
    },
    { immediate: true, flush: 'post' },
);

watch(() => route.fullPath, (path) => {
    if (tour.isOpen && path !== navigationTarget.value) tour.dismiss();
});

watch(() => tour.persistenceError, (failed) => {
    if (!failed) return;
    toast.add({
        severity: 'warn',
        summary: 'Product tour',
        detail: 'Your tour is closed, but we could not save this preference. It may appear again the next time you sign in.',
        life: 6000,
    });
});

onBeforeUnmount(() => {
    tour.dismiss();
    tour.cancelReplay();
});

return (_ctx: any,_cache: any) => {
  return (_unref(tour).isOpen)
    ? (_openBlock(), _createBlock(ProductTourOverlay, {
        key: 0,
        step: _unref(tour).phase === 'tour' ? _unref(tour).currentStep : null,
        progress: _unref(tour).stepIndex + 1,
        total: _unref(tour).steps.length,
        navigating: navigationTarget.value !== null,
        loading: _unref(tour).phase === 'loading',
        onNext: next,
        onDismiss: _cache[0] || (_cache[0] = ($event: any) => (_unref(tour).dismiss()))
      }, null, 8, ["step", "progress", "total", "navigating", "loading"]))
    : _createCommentVNode("", true)
}
}

})